import React, { useEffect, useState } from 'react';
import { Modal, Stack, TextInput, Textarea, Select, Button, Group, Text, Box } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import {
  usePostPerformanceReviewRounds,
  useGetPerformanceReviewQuestionnaires,
} from '../../api/generated/performance-review/performance-review';
import { PerformanceReviewQuestionnaire } from '../../api/generated/model/performanceReviewQuestionnaire';

interface Props {
  opened: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

/**
 * Evenly distribute three stage deadlines across an inclusive [start, end] range.
 * Slices the range into three equal chunks and returns the END date of each
 * chunk — these match the semantics of "by date X self-stage must be done".
 */
function splitDeadlines(
  start: Date,
  end: Date,
): {
  collectEssay: Date;
  collectFeedback: Date;
  summary: Date;
} {
  const ms = end.getTime() - start.getTime();
  const third = ms / 3;
  return {
    collectEssay: new Date(start.getTime() + third),
    collectFeedback: new Date(start.getTime() + 2 * third),
    summary: new Date(end),
  };
}

export const PerformanceReviewRoundCreateModal: React.FC<Props> = ({
  opened,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { data: questionnaires = [] } = useGetPerformanceReviewQuestionnaires();
  const create = usePostPerformanceReviewRounds();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questionnaire, setQuestionnaire] = useState<string>('');
  const [period, setPeriod] = useState<DatesRangeValue>([null, null]);
  const [essayDeadline, setEssayDeadline] = useState<Date | null>(null);
  const [feedbackDeadline, setFeedbackDeadline] = useState<Date | null>(null);
  const [summaryDeadline, setSummaryDeadline] = useState<Date | null>(null);

  // Auto-split whenever the full range changes (overwrites any prior edits —
  // simplest mental model; admin can tweak each stage afterwards).
  useEffect(() => {
    if (!period[0] || !period[1]) {
      setEssayDeadline(null);
      setFeedbackDeadline(null);
      setSummaryDeadline(null);
      return;
    }
    const s = period[0] instanceof Date ? period[0] : new Date(period[0]);
    const e = period[1] instanceof Date ? period[1] : new Date(period[1]);
    const split = splitDeadlines(s, e);
    setEssayDeadline(split.collectEssay);
    setFeedbackDeadline(split.collectFeedback);
    setSummaryDeadline(split.summary);
  }, [period]);

  const toIso = (v: Date | string | null): string => {
    if (!v) return '';
    return v instanceof Date ? v.toISOString() : new Date(v).toISOString();
  };

  const handleCreate = async () => {
    if (!title || !questionnaire || !period[0] || !period[1]) return;
    const created = await create.mutateAsync({
      data: {
        title,
        description,
        questionnaire,
        startDate: toIso(period[0]),
        endDate: toIso(period[1]),
        stageDeadlines: {
          ...(essayDeadline ? { collectEssay: toIso(essayDeadline) } : {}),
          ...(feedbackDeadline ? { collectFeedback: toIso(feedbackDeadline) } : {}),
          ...(summaryDeadline ? { summary: toIso(summaryDeadline) } : {}),
        },
      },
    });
    const createdRecord = created as unknown as { _id?: string };
    if (createdRecord?._id) {
      onCreated(createdRecord._id);
    }
    onClose();
  };

  const showStageDeadlines = !!(period[0] && period[1]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('perfReview.createRound')} size="lg">
      <Stack>
        <TextInput
          label={t('perfReview.formTitle')}
          placeholder={t('perfReview.formTitlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <Textarea
          label={t('perfReview.formDescription')}
          placeholder={t('perfReview.formDescriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          autosize
          minRows={2}
        />
        <Select
          label={t('perfReview.questionnaire')}
          placeholder={t('perfReview.questionnairePlaceholder')}
          required
          data={(questionnaires as unknown as PerformanceReviewQuestionnaire[]).map((q) => ({
            value: q._id ?? '',
            label: `${q.name} v${q.version}`,
          }))}
          value={questionnaire}
          onChange={(v) => setQuestionnaire(v || '')}
        />
        <DatePickerInput
          type="range"
          label={t('perfReview.periodLabel')}
          placeholder={t('perfReview.periodPlaceholder')}
          value={period}
          onChange={setPeriod}
        />
        {showStageDeadlines && (
          <Box
            p="md"
            style={{
              background: 'var(--mantine-color-default-hover)',
              borderRadius: 'var(--mantine-radius-md)',
            }}
          >
            <Text
              size="xs"
              c="dimmed"
              mb="sm"
              fw={500}
              tt="uppercase"
              style={{ letterSpacing: '0.08em' }}
            >
              {t('perfReview.stageDeadlines.title')}
            </Text>
            <Stack gap="xs">
              <DatePickerInput
                label={t('perfReview.stageDeadlines.collectEssay')}
                value={essayDeadline}
                onChange={(v) => setEssayDeadline(v ? new Date(v) : null)}
                clearable
                minDate={period[0] ? new Date(period[0]) : undefined}
                maxDate={period[1] ? new Date(period[1]) : undefined}
              />
              <DatePickerInput
                label={t('perfReview.stageDeadlines.collectFeedback')}
                value={feedbackDeadline}
                onChange={(v) => setFeedbackDeadline(v ? new Date(v) : null)}
                clearable
                minDate={period[0] ? new Date(period[0]) : undefined}
                maxDate={period[1] ? new Date(period[1]) : undefined}
              />
              <DatePickerInput
                label={t('perfReview.stageDeadlines.summary')}
                value={summaryDeadline}
                onChange={(v) => setSummaryDeadline(v ? new Date(v) : null)}
                clearable
                minDate={period[0] ? new Date(period[0]) : undefined}
                maxDate={period[1] ? new Date(period[1]) : undefined}
              />
            </Stack>
            <Text size="xs" c="dimmed" mt="sm">
              {t('perfReview.stageDeadlines.hint')}
            </Text>
          </Box>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('perfReview.cancel')}
          </Button>
          <Button onClick={handleCreate} loading={create.isPending}>
            {t('perfReview.create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
